/**
 * WS-14 — console GLOBAL SEARCH (the command palette).
 *
 * A proper modal dialog (role=dialog, aria-modal, the shared useFocusTrap, labelled by its input)
 * that finds CLIENTS / SERVICES / TEAM across the data the console already holds — no new API, no
 * /v1/search. The matching + RBAC exclusion live in ./engine.ts (runSearch); this file is the dialog
 * shell, the debounce, and the keyboard model:
 *   ↑/↓  move the active result (wraps), the list scrolls it into view
 *   Enter navigates to the active result (router navigate) and closes
 *   Esc   closes (handled by useFocusTrap) and restores focus to the opener
 *
 * Opened from the AppShell (a Search button + the Cmd/Ctrl-K shortcut). Results are bounded and the
 * empty/loading states are honest — it never invents a row.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { cx, useFocusTrap } from '@salonomy/ui';
import { useClients } from '../../data/clients.js';
import { useServices } from '../../data/services.js';
import { useStaff } from '../../data/salon.js';
import { useAuth } from '../../lib/auth.js';
import { MIN_QUERY, runSearch, type SearchResult } from './engine.js';

function useDebounced<T>(value: T, ms: number): T {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
}

export function CommandPalette({ open, onClose }: { open: boolean; onClose: () => void }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [q, setQ] = useState('');
  const [active, setActive] = useState(0);
  const debouncedQ = useDebounced(q, 200);
  const panelRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Trap focus, close on Escape, restore focus to the opener (the Search button) on close.
  useFocusTrap(panelRef, open, onClose);

  // Only hit the clients endpoint with a meaningful term (it searches name/phone server-side, RLS-
  // filtered); services + staff come from already-cached lists. Below the threshold we pass '' so the
  // clients query stays on its default (no per-keystroke fetch storm).
  const term = debouncedQ.trim().length >= MIN_QUERY ? debouncedQ.trim() : '';
  const { data: clientPage, isFetching: clientsFetching } = useClients(term);
  const { data: services } = useServices();
  const { data: staff } = useStaff();

  const results = useMemo(
    () =>
      runSearch(debouncedQ, {
        clients: clientPage?.data,
        services,
        staff,
        user: user ? { renterId: user.renterId } : null,
      }),
    [debouncedQ, clientPage, services, staff, user],
  );

  // Keep the active index in range as results change; reset to the top on a new query.
  useEffect(() => {
    setActive(0);
  }, [debouncedQ]);
  useEffect(() => {
    if (active >= results.length) setActive(0);
  }, [active, results.length]);

  // Reset on each open so the palette always opens clean and focused on the input.
  useEffect(() => {
    if (open) {
      setQ('');
      setActive(0);
    }
  }, [open]);

  const go = useCallback(
    (r: SearchResult | undefined) => {
      if (!r) return;
      onClose();
      void navigate({ to: r.to, ...(r.search ? { search: r.search as never } : {}) });
    },
    [navigate, onClose],
  );

  const onKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setActive((i) => (results.length === 0 ? 0 : (i + 1) % results.length));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setActive((i) => (results.length === 0 ? 0 : (i - 1 + results.length) % results.length));
      } else if (e.key === 'Enter') {
        e.preventDefault();
        go(results[active]);
      }
      // Escape is handled by useFocusTrap (single source of truth for the dialog).
    },
    [results, active, go],
  );

  if (!open) return null;

  const showMin = debouncedQ.trim().length > 0 && debouncedQ.trim().length < MIN_QUERY;
  const loading = term.length > 0 && clientsFetching && results.length === 0;
  const empty = term.length > 0 && !clientsFetching && results.length === 0;

  return (
    <div className="cmdk" role="presentation" onMouseDown={onClose}>
      <div
        ref={panelRef}
        className="cmdk__panel"
        role="dialog"
        aria-modal="true"
        aria-label="Search clients, services and team"
        onMouseDown={(e) => e.stopPropagation()}
        onKeyDown={onKeyDown}
      >
        <div className="cmdk__head">
          <span className="cmdk__icon" aria-hidden>
            ⌕
          </span>
          <input
            ref={inputRef}
            className="cmdk__input"
            type="search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search clients, services, team…"
            aria-label="Search clients, services and team"
            aria-controls="cmdk-results"
            aria-activedescendant={results[active] ? `cmdk-opt-${results[active]!.key}` : undefined}
            autoComplete="off"
            // eslint-disable-next-line jsx-a11y/no-autofocus
            autoFocus
          />
          <kbd className="cmdk__esc" aria-hidden>
            Esc
          </kbd>
        </div>

        <ResultsList
          results={results}
          active={active}
          onPick={go}
          onHover={setActive}
          showMin={showMin}
          loading={loading}
          empty={empty}
        />
      </div>
    </div>
  );
}

function ResultsList({
  results,
  active,
  onPick,
  onHover,
  showMin,
  loading,
  empty,
}: {
  results: SearchResult[];
  active: number;
  onPick: (r: SearchResult) => void;
  onHover: (i: number) => void;
  showMin: boolean;
  loading: boolean;
  empty: boolean;
}) {
  if (showMin) {
    return (
      <div className="cmdk__hint" role="status">
        Keep typing to search…
      </div>
    );
  }
  if (loading) {
    return (
      <div className="cmdk__hint" role="status">
        Searching…
      </div>
    );
  }
  if (empty) {
    return (
      <div className="cmdk__hint" role="status">
        No matches. Try a name, phone, service or team member.
      </div>
    );
  }
  if (results.length === 0) {
    return (
      <div className="cmdk__hint" role="status">
        Find a client, service or team member — type at least two characters.
      </div>
    );
  }

  // Render grouped, but keep ONE flat focus order (active index) so arrow keys cross groups cleanly.
  let lastGroup = '';
  return (
    <ul className="cmdk__results" id="cmdk-results" role="listbox" aria-label="Search results">
      {results.map((r, i) => {
        const header = r.group !== lastGroup ? r.group : null;
        lastGroup = r.group;
        return (
          <li key={r.key} role="presentation">
            {header ? (
              <div className="cmdk__group" role="presentation">
                {header}
              </div>
            ) : null}
            <div
              id={`cmdk-opt-${r.key}`}
              role="option"
              aria-selected={i === active}
              className={cx('cmdk__opt', i === active && 'cmdk__opt--active')}
              onMouseEnter={() => onHover(i)}
              onMouseDown={(e) => {
                e.preventDefault();
                onPick(r);
              }}
            >
              <span className="cmdk__opt-title">{r.title}</span>
              {r.subtitle ? <span className="cmdk__opt-sub">{r.subtitle}</span> : null}
            </div>
          </li>
        );
      })}
    </ul>
  );
}
